import { sequence } from '0xsequence';
import { networks } from '@0xsequence/network';
import { UserRejectedRequestError, createWalletClient, custom } from 'viem';
import { Connector, ConnectorNotFoundError } from 'wagmi';

class SequenceConnector extends Connector {
  id = "sequence";
  name = "Sequence";
  // chains = chainConfigList
  ready = true;
  provider = null;
  wallet;
  connected = false;
  constructor({ chains, options }) {
    super({ chains, options });
  }
  async connect() {
    await this.initWallet();
    if (!this.wallet.isConnected()) {
      this?.emit("message", { type: "connecting" });
      const e = await this.wallet.connect(this.options?.connect);
      if (e.error) {
        throw new UserRejectedRequestError(new Error(e.error));
      }
      if (!e.connected) {
        throw new UserRejectedRequestError(new Error("Wallet connection rejected"));
      }
    }
    const chainId = await this.getChainId();
    const provider = await this.getProvider();
    const account = await this.getAccount();
    provider.on("accountsChanged", this.onAccountsChanged);
    this.wallet.on("chainChanged", this.onChainChanged);
    provider.on("disconnect", this.onDisconnect);
    this.connected = true;
    return {
      account,
      chain: {
        id: chainId,
        unsupported: this.isChainUnsupported(chainId)
      }
    };
  }
  async getWalletClient({ chainId } = {}) {
    const [provider, account] = await Promise.all([
      this.getProvider(),
      this.getAccount()
    ]);
    const chain = this.chains.find((x) => x.id === chainId);
    if (!provider)
      throw new Error("provider is required.");
    return createWalletClient({
      account,
      chain,
      transport: custom(provider)
    });
  }
  async disconnect() {
    await this.initWallet();
    this.wallet.disconnect();
  }
  async getAccount() {
    await this.initWallet();
    return this.wallet.getAddress();
  }
  async getChainId() {
    await this.initWallet();
    if (!this.wallet.isConnected()) {
      return this.connect().then(() => this.wallet.getChainId());
    }
    return this.wallet.getChainId();
  }
  async getProvider() {
    await this.initWallet();
    if (!this.provider) {
      const provider = this.wallet.getProvider();
      if (!provider) {
        throw new ConnectorNotFoundError("Failed to get Sequence Wallet provider.");
      }
      this.provider = provider;
    }
    return this.provider;
  }
  async getSigner() {
    await this.initWallet();
    return this.wallet.getSigner();
  }
  async isAuthorized() {
    try {
      const account = await this.getAccount();
      return !!account;
    } catch {
      return false;
    }
  }
  async switchChain(chainId) {
    await this.provider?.send("wallet_switchEthereumChain", [{ chainId }]);
    return { id: chainId };
  }
  onAccountsChanged = (accounts) => {
    return { account: accounts[0] };
  };
  onChainChanged = (chain) => {
    this.provider?.emit("chainChanged", chain);
    const id = normalizeChainId(chain);
    const unsupported = this.isChainUnsupported(id);
    this?.emit("change", { chain: { id, unsupported } });
  };
  onDisconnect = () => {
    this?.emit("disconnect");
  };
  isChainUnsupported(chainId) {
    return !(chainId in networks);
  }
  async initWallet() {
    if (!this.wallet) {
      const walletAppURL = this.options.connect?.walletAppURL;
      if (walletAppURL) {
        this.wallet = await sequence.initWallet(void 0, { walletAppURL });
      } else {
        this.wallet = await sequence.initWallet();
      }
    }
  }
}
function normalizeChainId(chainId) {
  if (typeof chainId === "string")
    return Number.parseInt(chainId, chainId.trim().substring(0, 2) === "0x" ? 16 : 10);
  if (typeof chainId === "bigint")
    return Number(chainId);
  return chainId;
}

export { SequenceConnector };
//# sourceMappingURL=sequence-wagmi-connector.js.map
